// src/pages/Exams/generate/hooks/useExamTemplates.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExamGenApi } from '../client';
import type { Blueprint } from '../types';

const KEY = ['exam_gen', 'templates'];

export function useExamTemplates() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: KEY, queryFn: () => ExamGenApi.listTemplates() });

  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });

  const create = useMutation({
    mutationFn: (vars: { name: string; blueprint: Blueprint }) =>
      ExamGenApi.createTemplate(vars.name, vars.blueprint),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: (vars: { id: number; patch: { name?: string; blueprint?: Blueprint } }) =>
      ExamGenApi.updateTemplate(vars.id, vars.patch),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: number) => ExamGenApi.deleteTemplate(id),
    onSuccess: invalidate,
  });
  const share = useMutation({
    mutationFn: (id: number) => ExamGenApi.shareTemplate(id),
    onSuccess: invalidate,
  });

  // These callbacks are fired without await from the TemplatesTab / page; catch
  // so a failed mutation logs instead of surfacing as an unhandled rejection.
  const logErr = (e: unknown) => { console.error('[exam_gen templates]', e); };

  return {
    templates: list.data ?? [],
    isLoading: list.isLoading,
    error: list.error,
    createTemplate: (name: string, blueprint: Blueprint) =>
      create.mutateAsync({ name, blueprint }).catch(logErr),
    renameTemplate: (id: number, name: string) =>
      update.mutateAsync({ id, patch: { name } }).catch(logErr),
    deleteTemplate: (id: number) => remove.mutateAsync(id).catch(logErr),
    shareTemplate: (id: number) => share.mutateAsync(id).catch(logErr),
  };
}
